// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useI18n } from "../../context/I18nContext";
import { listLastUserCompanies } from "../api/services/LastUserCompanyService";
import { getDirectoryUser } from "../../features/users/api/users-service";
import { resolveBadgeCompanyName } from "../utils/badgeCard";
import { buildBadgeCardPdfBlob } from "../utils/badgeCardPdf";
import { ACTIVE_EVENT_SELECTION_CHANGED, readSelectedActiveEventId } from "../../features/events/active-events";
import { buildBadgeQrData, ensureMyBadge, getMyBadge, toBadgeCardDesign } from "../utils/badgeQr";

const extractList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const direct = payload?.data ?? payload?.items ?? payload?.result;
  if (Array.isArray(direct)) return direct;
  const nested = direct?.data ?? direct?.items ?? direct?.result;
  if (Array.isArray(nested)) return nested;
  return [];
};

const getStoredActiveBusinessId = () => {
  try {
    const stored = localStorage.getItem("activeEvent");
    const parsed = stored ? JSON.parse(stored) : null;
    return String(parsed?.activeBusinessId || parsed?.businessId || "").trim();
  } catch {
    return "";
  }
};

const getStoredActiveEventId = () => {
  const selected = readSelectedActiveEventId();
  if (selected) return selected;
  try {
    const stored = localStorage.getItem("activeEvent");
    const parsed = stored ? JSON.parse(stored) : null;
    return String(parsed?.activeEventId || parsed?.eventId || "").trim();
  } catch {
    return "";
  }
};

const getStoredUserInfo = () => {
  try {
    const stored = localStorage.getItem("userDetails");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.user ?? parsed ?? null;
  } catch {
    return null;
  }
};

const USER_DETAILS_UPDATED_EVENT = "user-details-updated";

/** Anasayfadaki "Baslarken" listesi (Faz 4.1) yaka karti adimini bu bayraktan okur. */
export const ONBOARDING_UPDATED_EVENT = "wc:onboarding-updated";

const onboardingBadgeKeyFor = (userId) => `wc.onboarding.badgeDone.${userId}`;

export const readOnboardingBadgeDone = (userId) => {
  if (!userId) return false;
  try {
    return localStorage.getItem(onboardingBadgeKeyFor(userId)) === "1";
  } catch {
    return false;
  }
};

const markOnboardingBadgeDone = (userId) => {
  if (!userId) return;
  try {
    localStorage.setItem(onboardingBadgeKeyFor(userId), "1");
  } catch {
    // Depolama kapaliysa bayrak tutulamaz; indirme yine de basarilidir.
  }
  window.dispatchEvent(new Event(ONBOARDING_UPDATED_EVENT));
};

const formatBadgePdfTimestamp = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

/**
 * "Yaka Kartı" PDF indirme akışı — hem header dropdown'unda hem de anasayfa
 * kısayolunda aynı davranışı üretmesi gerektiği için buraya çıkarıldı.
 */
export function useBadgeCardDownload() {
  const { t, locale } = useI18n();
  const [userInfo, setUserInfo] = useState(() => getStoredUserInfo());
  const [directoryUser, setDirectoryUser] = useState(null);
  const [ownedCompanies, setOwnedCompanies] = useState([]);
  const [ownedCompaniesLoaded, setOwnedCompaniesLoaded] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);
  useEffect(() => {
    const bump = () => setSelectionVersion((v) => v + 1);
    window.addEventListener(ACTIVE_EVENT_SELECTION_CHANGED, bump);
    return () => window.removeEventListener(ACTIVE_EVENT_SELECTION_CHANGED, bump);
  }, []);
  const [myBadge, setMyBadge] = useState(null);
  const [isBadgeDownloading, setIsBadgeDownloading] = useState(false);
  /*
   * Yeni sekme, tarayicinin "kullanici jesti" penceresi kapanmadan (senkron
   * olarak) acilmali, yoksa pop-up engelleyiciye takilir. PDF'in kendisi
   * asenkron uretildigi icin sekme BOS acilip token/PDF hazir olunca
   * icine yonlendiriliyor.
   */
  const previewWindowRef = useRef(null);

  useEffect(() => {
    const handleUserDetailsUpdated = () => setUserInfo(getStoredUserInfo());
    window.addEventListener("storage", handleUserDetailsUpdated);
    window.addEventListener(USER_DETAILS_UPDATED_EVENT, handleUserDetailsUpdated);
    return () => {
      window.removeEventListener("storage", handleUserDetailsUpdated);
      window.removeEventListener(USER_DETAILS_UPDATED_EVENT, handleUserDetailsUpdated);
    };
  }, []);

  const currentUserId = useMemo(
    () => userInfo?.id ?? userInfo?.user?.id ?? userInfo?.userId ?? "",
    [userInfo],
  );

  const displayName = userInfo
    ? `${userInfo.firstName ?? ""} ${userInfo.lastName ?? ""}`.trim()
    : "";

  useEffect(() => {
    if (!currentUserId) {
      setOwnedCompanies([]);
      setOwnedCompaniesLoaded(true);
      setDirectoryUser(null);
      return;
    }
    let active = true;
    setOwnedCompaniesLoaded(false);
    listLastUserCompanies({ mine: true })
      .then((response) => {
        if (active) setOwnedCompanies(extractList(response?.data));
      })
      .catch(() => {
        if (active) setOwnedCompanies([]);
      })
      .finally(() => {
        if (active) setOwnedCompaniesLoaded(true);
      });
    getDirectoryUser(currentUserId)
      .then((response) => {
        if (active) setDirectoryUser(response?.data?.data ?? response?.data ?? null);
      })
      .catch(() => {
        if (active) setDirectoryUser(null);
      });
    return () => {
      active = false;
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) {
      setMyBadge(null);
      return;
    }
    let active = true;
    getMyBadge(getStoredActiveEventId(), getStoredActiveBusinessId())
      .then((badge) => {
        if (active) setMyBadge(badge);
      })
      .catch(() => {
        if (active) setMyBadge(null);
      });
    return () => {
      active = false;
    };
  }, [currentUserId, selectionVersion]);

  const badgeCompanyName = useMemo(
    () => resolveBadgeCompanyName(directoryUser, ownedCompanies, getStoredActiveBusinessId()),
    [directoryUser, ownedCompanies],
  );

  const badgePrintName = useMemo(
    () => (myBadge ? `${myBadge.name} ${myBadge.surname}`.trim() : displayName),
    [myBadge, displayName],
  );
  const badgePrintCompany = useMemo(
    () => myBadge?.company || badgeCompanyName,
    [myBadge, badgeCompanyName],
  );

  /*
   * QR, PDF icinde `badge.badgeUrl`'den cizilir. Kart yoksa profil adresine
   * (`/user/{userId}`) GERI DUSULMEZ — bkz. handleBadgeDownload.
   */
  const buildAndDownloadBadge = async (badge) => {
    const printName = `${badge.name} ${badge.surname}`.trim() || badgePrintName;

    try {
      const pdfBlob = await buildBadgeCardPdfBlob({
        fullName: printName,
        companyName: badge.company || badgePrintCompany,
        fallbackCompanyName: "",
        badgeType: badge.badgeType,
        qrToken: badge.qrToken,
        badgeUrl: badge.badgeUrl || buildBadgeQrData(badge.qrToken),
        qrLogoSrc: "/expo_qr_icon.png",
        design: toBadgeCardDesign(badge.design),
      });
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      const safeName = printName
        .toLocaleLowerCase(locale === "tr" ? "tr-TR" : "en-US")
        .replace(/[^a-z0-9\s-]/gi, "")
        .trim()
        .replace(/\s+/g, "-") || "badge-card";
      link.href = url;
      link.download = `${t("header.badgeCard.filePrefix")}-${safeName}-${formatBadgePdfTimestamp()}.pdf`;
      link.click();

      const previewWindow = previewWindowRef.current;
      previewWindowRef.current = null;
      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
      /* Yeni sekmenin PDF'i tarayicidan yuklemesine zaman taniyoruz; hemen
       * revoke edilirse sekme bos acilabilir. */
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);

      toast.success(t("header.badgeCard.downloadSuccess"));
      markOnboardingBadgeDone(currentUserId);
    } catch {
      previewWindowRef.current?.close();
      previewWindowRef.current = null;
      toast.error(t("header.badgeCard.downloadError"));
    } finally {
      setIsBadgeDownloading(false);
    }
  };

  const handleBadgeDownload = async () => {
    if (isBadgeDownloading) return;

    if (!currentUserId) {
      toast.error(t("header.badgeCard.missingQr"));
      return;
    }

    /*
     * Sekme burada, henuz hicbir await calismadan (kullanici jesti hala
     * aktifken) aciliyor — asagida PDF hazir olunca icine yonlendirilecek.
     */
    previewWindowRef.current = window.open("", "_blank");

    setIsBadgeDownloading(true);

    /*
     * Sunucu kart varsa token'i korur, ad/soyad/firma alanlarini gunceller ve
     * PDF'i o taze veriyle uretmemiz icin mevcut karti geri doner.
     */
    /*
     * Kart her indirmede yeniden uretilir/tazelenir (idempotent: varsa token
     * korunur). Ilk deneme basarisiz olursa bir kez daha denenir; yine
     * uretilemezse onceden yuklenmis kart kullanilir, o da yoksa PDF basilmaz.
     */
    const eventId = getStoredActiveEventId();
    const businessId = getStoredActiveBusinessId();
    const ensuredBadge =
      (await ensureMyBadge(eventId, businessId)) ?? (await ensureMyBadge(eventId, businessId));
    const badge = ensuredBadge ?? (myBadge?.qrToken ? myBadge : null);
    if (ensuredBadge) setMyBadge(ensuredBadge);

    if (!badge) {
      toast.error(t("header.badgeCard.missingQr"));
      previewWindowRef.current?.close();
      previewWindowRef.current = null;
      setIsBadgeDownloading(false);
      return;
    }

    await buildAndDownloadBadge(badge);
  };

  // Geriye donuk uyumluluk: QR artik PDF icinde uretiliyor, gizli canvas yok.
  const badgeCanvas = null;

  return {
    isBadgeDownloading,
    handleBadgeDownload,
    badgeCanvas,
    ownedCompanyCount: ownedCompanies.length,
    ownedCompaniesLoaded,
  };
}

export default useBadgeCardDownload;
